import { useState, useRef } from 'react';

interface ComparisonItem {
  id: number;
  title: string;
  category: string;
  beforeImage: string;
  afterImage: string;
}

const comparisonItems: ComparisonItem[] = [
  {
    id: 1,
    title: 'Modern Living Room',
    category: 'Virtual Staging',
    beforeImage: 'https://public.readdy.ai/ai/img_res/c3b9bbd80946b44aff830590e9aeb120.jpg',
    afterImage: 'https://readdy.ai/api/search-image?query=beautifully%20staged%20modern%20living%20room%20with%20elegant%20gray%20sofa%20coffee%20table%20plants%20artwork%20cozy%20decor%20hardwood%20floors%20large%20windows%20natural%20light%20professional%20real%20estate%20photography&width=600&height=400&seq=ba2&orientation=landscape',
  },
  {
    id: 2,
    title: 'Luxury Kitchen',
    category: 'Photo Enhancement',
    beforeImage: 'https://readdy.ai/api/search-image?query=dark%20underexposed%20kitchen%20interior%20poor%20lighting%20shadows%20granite%20countertops%20white%20cabinets%20needs%20color%20correction%20real%20estate%20photo%20before%20editing&width=600&height=400&seq=ba3&orientation=landscape',
    afterImage: 'https://readdy.ai/api/search-image?query=bright%20luminous%20luxury%20kitchen%20perfect%20lighting%20white%20cabinets%20granite%20countertops%20stainless%20steel%20appliances%20professional%20real%20estate%20photography%20HDR%20enhanced%20warm%20inviting&width=600&height=400&seq=ba4&orientation=landscape',
  },
  {
    id: 3,
    title: 'Master Bedroom',
    category: 'Virtual Staging',
    beforeImage: 'https://readdy.ai/api/search-image?query=empty%20vacant%20master%20bedroom%20beige%20carpet%20white%20walls%20large%20window%20with%20blinds%20unfurnished%20real%20estate%20photography%20bare%20room%20needs%20staging&width=600&height=400&seq=ba5&orientation=landscape',
    afterImage: 'https://readdy.ai/api/search-image?query=elegantly%20staged%20master%20bedroom%20king%20size%20bed%20white%20bedding%20nightstands%20lamps%20artwork%20plants%20cozy%20luxurious%20decor%20professional%20real%20estate%20photography%20warm%20inviting&width=600&height=400&seq=ba6&orientation=landscape',
  },
  {
    id: 4,
    title: 'Backyard Patio',
    category: 'Sky Replacement',
    beforeImage: 'https://readdy.ai/api/search-image?query=backyard%20patio%20outdoor%20space%20overcast%20gray%20cloudy%20sky%20dull%20lighting%20wooden%20deck%20furniture%20needs%20sky%20replacement%20real%20estate%20exterior%20photography&width=600&height=400&seq=ba7&orientation=landscape',
    afterImage: 'https://readdy.ai/api/search-image?query=stunning%20backyard%20patio%20beautiful%20blue%20sky%20white%20clouds%20golden%20hour%20lighting%20wooden%20deck%20outdoor%20furniture%20vibrant%20colors%20professional%20real%20estate%20exterior%20photography&width=600&height=400&seq=ba8&orientation=landscape',
  },
];

function ComparisonSlider({ item }: { item: ComparisonItem }) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging.current) {
      handleMove(e.clientX);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  };

  return (
    <div className="group">
      <div
        ref={containerRef}
        className="relative w-full h-72 md:h-80 rounded-xl overflow-hidden cursor-ew-resize select-none shadow-lg"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
      >
        {/* Before Image (Background) */}
        <img
          src={item.beforeImage}
          alt={`${item.title} - Before`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />

        {/* After Image (Clipped) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${sliderPosition}%` }}
        >
          <img
            src={item.afterImage}
            alt={`${item.title} - After`}
            className="absolute inset-0 w-full h-full object-cover object-top"
            style={{ width: containerRef.current?.offsetWidth || '100%' }}
          />
        </div>

        {/* Slider Line */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-10"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        >
          {/* Slider Handle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
            <div className="flex items-center gap-0.5">
              <i className="ri-arrow-left-s-line text-gray-700 text-lg"></i>
              <i className="ri-arrow-right-s-line text-gray-700 text-lg"></i>
            </div>
          </div>
        </div>

        {/* Labels */}
        <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
          Before
        </div>
        <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
          After
        </div>
      </div>

      {/* Info */}
      <div className="mt-4">
        <span className="text-teal-600 text-sm font-medium">{item.category}</span>
        <h4 className="text-gray-900 font-semibold mt-1">{item.title}</h4>
      </div>
    </div>
  );
}

export default function BeforeAfterGallery() {
  return (
    <section id="before-after" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-teal-600 font-semibold text-sm tracking-wide uppercase">
            Transformations
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
            See the Difference
          </h2>
          <p className="text-gray-600 text-lg">
            Drag the slider to compare before and after results. Our professional editing transforms ordinary photos into stunning visuals that sell properties faster.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {comparisonItems.map((item) => (
            <ComparisonSlider key={item.id} item={item} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <p className="text-gray-600 mb-4">
            Ready to transform your property photos?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Get Started Today
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
